import { z } from 'zod';
import { isoDateSchema, paginationQuerySchema } from './common.js';
import { gameProfileDefinitionSchema } from './game-profile.js';
import { serverStatusSchema } from './infra.js';
import { isPermission, type Permission } from './permissions.js';

/** Power state of a server process, as hearthd reports it. */
export const powerStateSchema = z.enum(['offline', 'starting', 'running', 'stopping']);
export type PowerState = z.infer<typeof powerStateSchema>;

/** Live resource usage. CPU is relative to one core (200 = two cores busy). */
export const serverStatsSchema = z.object({
  state: powerStateSchema,
  cpuPercent: z.number(),
  memoryBytes: z.number(),
  memoryLimitBytes: z.number(),
  networkRxBytes: z.number(),
  networkTxBytes: z.number(),
  diskBytes: z.number(),
  uptimeMs: z.number(),
  /** The game's own player count; null when it can't be asked, isn't ready or didn't answer. */
  players: z.object({ online: z.number(), max: z.number() }).nullable(),
});
export type ServerStats = z.infer<typeof serverStatsSchema>;

/** hearthd's stats payload (snake_case), as sent on the websocket and by /resources. */
export const daemonStatsSchema = z
  .object({
    state: powerStateSchema,
    cpu_percent: z.number(),
    memory_bytes: z.number(),
    memory_limit_bytes: z.number(),
    network_rx_bytes: z.number(),
    network_tx_bytes: z.number(),
    disk_bytes: z.number(),
    uptime_ms: z.number(),
    players: z.object({ online: z.number(), max: z.number() }).nullish(),
  })
  .transform((s): ServerStats => ({
    state: s.state,
    cpuPercent: s.cpu_percent,
    memoryBytes: s.memory_bytes,
    memoryLimitBytes: s.memory_limit_bytes,
    networkRxBytes: s.network_rx_bytes,
    networkTxBytes: s.network_tx_bytes,
    diskBytes: s.disk_bytes,
    uptimeMs: s.uptime_ms,
    players: s.players ?? null,
  }));

/**
 * How hearthd can ask a running game for its players, from the egg: Minecraft Java Edition
 * (the "eula" feature) and Rust (an egg that starts RustDedicated, which speaks WebRCON).
 */
export const playerQueryFor = (
  features: readonly string[],
  startup: string | null = null,
): 'minecraft-java' | 'rust' | null => {
  if (features.includes('eula')) return 'minecraft-java';
  if (startup && /\bRustDedicated\b/.test(startup)) return 'rust';
  return null;
};

export const clientServerSchema = z.object({
  id: z.number().int(),
  uuid: z.string(),
  identifier: z.string(),
  name: z.string(),
  description: z.string(),
  status: serverStatusSchema,
  isOwner: z.boolean(),
  owner: z.object({ username: z.string() }),
  node: z.object({
    name: z.string(),
    /** The address the node is reached at. */
    fqdn: z.string(),
    /** The location the administrator assigned to the node. */
    location: z.object({ short: z.string(), long: z.string().nullable() }),
  }),
  egg: z.object({
    name: z.string(),
    features: z.array(z.string()),
    /** Whether the player count can be read from this game (see playerQueryFor). */
    playerQuery: z.boolean(),
  }),
  /** How this server's game is presented; resolved by the API, never re-derived by clients. */
  profile: gameProfileDefinitionSchema,
  allocation: z.object({ ip: z.string(), ipAlias: z.string().nullable(), port: z.number().int() }),
  limits: z.object({
    memoryMb: z.number().int(),
    swapMb: z.number().int(),
    diskMb: z.number().int(),
    cpuPercent: z.number().int(),
  }),
  autoRestart: z.boolean(),
  installed: z.boolean(),
  sftp: z.object({ host: z.string(), port: z.number().int(), username: z.string() }),
  /** The caller's permissions on this server; owners and admins hold "*". */
  permissions: z.array(z.string()),
});
export type ClientServer = z.infer<typeof clientServerSchema>;

export const clientServerListQuerySchema = paginationQuerySchema.extend({
  /** "all" (admins only) lists every server, not just the caller's. */
  type: z.enum(['mine', 'all']).default('mine'),
  search: z.string().trim().max(191).optional(),
});

/** A short-lived token for the server's websocket on its node. */
export const websocketCredentialsSchema = z.object({
  token: z.string(),
  socket: z.string(),
  expiresAt: isoDateSchema,
});
export type WebsocketCredentials = z.infer<typeof websocketCredentialsSchema>;

export const serverCommandSchema = z.object({
  command: z
    .string()
    .trim()
    .min(1)
    .max(4096)
    .refine((c) => !/[\r\n]/.test(c), 'Send one command at a time.'),
});

export const acceptEulaSchema = z.object({ accept: z.literal(true) });

export const clientServerSettingsSchema = z.object({ autoRestart: z.boolean() });

/** A startup variable as the server's users see it (only variables the egg marks viewable). */
export const startupVariableSchema = z.object({
  envVariable: z.string(),
  name: z.string(),
  description: z.string(),
  value: z.string(),
  defaultValue: z.string(),
  rules: z.string(),
  isEditable: z.boolean(),
});
export type StartupVariable = z.infer<typeof startupVariableSchema>;

export const startupViewSchema = z.object({
  /** The command with {{VARIABLE}} placeholders, as the egg or admin wrote it. */
  startupCommand: z.string(),
  /** The command with the variables users may see filled in. */
  renderedCommand: z.string(),
  variables: z.array(startupVariableSchema),
  /** Label → image, from the egg. */
  dockerImages: z.record(z.string(), z.string()),
  image: z.string(),
});
export type StartupView = z.infer<typeof startupViewSchema>;

export const updateStartupVariableSchema = z.object({
  key: z.string().trim().min(1).max(191),
  value: z.string().max(10_000),
});

export const updateStartupImageSchema = z.object({
  image: z.string().trim().min(1).max(255),
});

/** Events on hearthd's websocket ({ event, args }). */
export const SOCKET_EVENTS = {
  auth: 'auth',
  authSuccess: 'auth success',
  jwtError: 'jwt error',
  tokenExpiring: 'token expiring',
  tokenExpired: 'token expired',
  sendCommand: 'send command',
  setState: 'set state',
  sendLogs: 'send logs',
  sendStats: 'send stats',
  status: 'status',
  consoleOutput: 'console output',
  installOutput: 'install output',
  installStarted: 'install started',
  installCompleted: 'install completed',
  stats: 'stats',
  daemonMessage: 'daemon message',
  daemonError: 'daemon error',
} as const;

// ---- Subusers ------------------------------------------------------------------------------

/** What a subuser may do; websocket.connect is always included so the console can open. */
export const subuserPermissionsSchema = z
  .array(z.string())
  .max(100)
  .superRefine((list, ctx) => {
    for (const p of list) {
      if (!isPermission(p)) ctx.addIssue({ code: 'custom', message: `Unknown permission "${p}".` });
    }
  })
  .transform((list) => [...new Set([...list, 'websocket.connect'])].sort());

export const subuserSchema = z.object({
  /** The user's UUID. */
  id: z.string(),
  username: z.string(),
  email: z.string(),
  permissions: z.array(z.string()),
  twoFactorEnabled: z.boolean(),
  createdAt: isoDateSchema,
});
export type Subuser = z.infer<typeof subuserSchema>;

export const createSubuserSchema = z.object({
  email: z.string().trim().toLowerCase().email().max(254),
  permissions: subuserPermissionsSchema,
});

export const updateSubuserSchema = z.object({ permissions: subuserPermissionsSchema });

/** Returned with a created subuser: whether a new account had to be made for the invite. */
export const createdSubuserSchema = subuserSchema.extend({ accountCreated: z.boolean() });

// ---- Backups -------------------------------------------------------------------------------

export const backupSchema = z.object({
  uuid: z.string(),
  name: z.string(),
  ignoredFiles: z.string(),
  disk: z.enum(['local', 's3']),
  checksum: z.string().nullable(),
  bytes: z.number(),
  /** null while the backup is still running. */
  isSuccessful: z.boolean().nullable(),
  isLocked: z.boolean(),
  createdAt: isoDateSchema,
  completedAt: isoDateSchema.nullable(),
});
export type Backup = z.infer<typeof backupSchema>;

export const createBackupSchema = z.object({
  name: z.string().trim().max(191).optional(),
  /** Gitignore-style patterns, one per line. */
  ignored: z.string().max(10_000).default(''),
});

export const restoreBackupSchema = z.object({
  /** Delete everything in the server's folder first, so it matches the backup exactly. */
  truncate: z.boolean().default(false),
});

export const backupLinkSchema = z.object({ url: z.string(), expiresAt: isoDateSchema });

// ---- Schedules -----------------------------------------------------------------------------

export const SCHEDULE_ACTIONS = ['command', 'power', 'backup'] as const;
export const POWER_ACTIONS = ['start', 'stop', 'restart', 'kill'] as const;
export const MAX_SCHEDULE_TASKS = 20;
/** The longest wait before a task, in seconds. */
export const MAX_TASK_OFFSET_SECONDS = 900;

export const cronFieldsSchema = z.object({
  minute: z.string().trim().max(100),
  hour: z.string().trim().max(100),
  dayOfMonth: z.string().trim().max(100),
  month: z.string().trim().max(100),
  dayOfWeek: z.string().trim().max(100),
});

export const scheduleTaskSchema = z.object({
  id: z.number().int(),
  sequence: z.number().int(),
  action: z.enum(SCHEDULE_ACTIONS),
  payload: z.string(),
  timeOffsetSeconds: z.number().int(),
  continueOnFailure: z.boolean(),
});
export type ScheduleTask = z.infer<typeof scheduleTaskSchema>;

export const scheduleSchema = z.object({
  id: z.number().int(),
  name: z.string(),
  cron: cronFieldsSchema,
  isActive: z.boolean(),
  isProcessing: z.boolean(),
  onlyWhenOnline: z.boolean(),
  lastRunAt: isoDateSchema.nullable(),
  nextRunAt: isoDateSchema.nullable(),
  tasks: z.array(scheduleTaskSchema),
});
export type Schedule = z.infer<typeof scheduleSchema>;

export const scheduleInputSchema = z.object({
  name: z.string().trim().min(1).max(191),
  cron: cronFieldsSchema,
  isActive: z.boolean().default(true),
  onlyWhenOnline: z.boolean().default(false),
});

export const scheduleTaskInputSchema = z
  .object({
    action: z.enum(SCHEDULE_ACTIONS),
    payload: z.string().max(10_000),
    timeOffsetSeconds: z.number().int().min(0).max(MAX_TASK_OFFSET_SECONDS).default(0),
    continueOnFailure: z.boolean().default(false),
  })
  .superRefine((t, ctx) => {
    if (t.action === 'command' && !t.payload.trim())
      ctx.addIssue({ code: 'custom', path: ['payload'], message: 'Enter the command to send.' });
    if (t.action === 'command' && t.payload.length > 4096)
      ctx.addIssue({
        code: 'custom',
        path: ['payload'],
        message: 'Commands are limited to 4096 characters.',
      });
    if (t.action === 'power' && !(POWER_ACTIONS as readonly string[]).includes(t.payload))
      ctx.addIssue({
        code: 'custom',
        path: ['payload'],
        message: 'Choose start, stop, restart or kill.',
      });
  });

/** The permission needed to do what a task does. Creating a task requires it too. */
export function permissionForTask(
  action: (typeof SCHEDULE_ACTIONS)[number],
  payload: string,
): Permission {
  if (action === 'backup') return 'backup.create';
  if (action === 'command') return 'control.console';
  return (
    (
      {
        start: 'control.start',
        restart: 'control.restart',
        stop: 'control.stop',
        kill: 'control.stop',
      } as const
    )[payload as 'start'] ?? 'control.stop'
  );
}

// ---- Server databases ----------------------------------------------------------------------

export const serverDatabaseSchema = z.object({
  id: z.number().int(),
  name: z.string(),
  username: z.string(),
  remote: z.string(),
  host: z.object({ address: z.string(), port: z.number().int() }),
  /** Only with database.view_password (and always in the response that creates or rotates it). */
  password: z.string().nullable(),
  createdAt: isoDateSchema,
});
export type ServerDatabase = z.infer<typeof serverDatabaseSchema>;

export const createServerDatabaseSchema = z.object({
  /** Added after the server's prefix: letters, numbers and underscores. */
  name: z
    .string()
    .trim()
    .min(1)
    .max(24)
    .regex(/^[A-Za-z0-9_]+$/, 'Use letters, numbers and underscores.'),
  /** Where the user may connect from; "%" means anywhere. */
  remote: z
    .string()
    .trim()
    .min(1)
    .max(100)
    .regex(/^[A-Za-z0-9_.%:-]+$/, 'Use an address or % for anywhere.')
    .default('%'),
});

// ---- Network allocations -------------------------------------------------------------------

export const serverAllocationSchema = z.object({
  id: z.number().int(),
  ip: z.string(),
  ipAlias: z.string().nullable(),
  port: z.number().int(),
  notes: z.string().nullable(),
  isPrimary: z.boolean(),
});
export type ServerAllocation = z.infer<typeof serverAllocationSchema>;

export const allocationListSchema = z.object({
  data: z.array(serverAllocationSchema),
  /** How many additional (non-primary) ports the server may have. */
  limit: z.number().int(),
});

export const updateAllocationNotesSchema = z.object({
  notes: z.string().trim().max(191).nullable(),
});

// ---------- live data (players) ----------

export const LIVE_STATUSES = ['ready', 'offline', 'unsupported', 'unreachable'] as const;
export const RCON_STATES = ['ready', 'not_configured', 'restart_required', 'unreachable'] as const;
/** Where a game's RCON is configured: a file the panel can edit, or the egg's variables. */
export const RCON_SETUPS = ['server_properties', 'startup'] as const;

/** Player actions hearthd will run; each maps to fixed command templates per game. */
export const PLAYER_ACTIONS = [
  'kick',
  'ban',
  'pardon',
  'op',
  'deop',
  'whitelist-add',
  'whitelist-remove',
  'unban',
] as const;
export type PlayerAction = (typeof PLAYER_ACTIONS)[number];

/** hearthd's live answer (snake_case) converted for the API. */
export const daemonLiveSchema = z
  .object({
    status: z.enum(LIVE_STATUSES),
    rcon: z.enum(RCON_STATES),
    online: z.number().int(),
    max: z.number().int(),
    players: z.array(z.string()),
    players_complete: z.boolean(),
    version: z.string().optional(),
    motd: z.string().optional(),
    detail: z.string().optional(),
    // Added with Rust; optional so an older hearthd still parses.
    adapter: z.string().optional(),
    setup: z.enum(RCON_SETUPS).optional(),
    missing: z.array(z.string()).optional(),
    actions: z.array(z.string()).optional(),
    roster: z
      .array(
        z.object({
          id: z.string(),
          name: z.string(),
          ping_ms: z.number().int(),
          connected_seconds: z.number().int(),
        }),
      )
      .optional(),
    queued: z.number().int().optional(),
  })
  .transform((l) => ({
    status: l.status,
    rcon: l.rcon,
    online: l.online,
    max: l.max,
    players: l.players,
    playersComplete: l.players_complete,
    version: l.version ?? null,
    motd: l.motd ?? null,
    detail: l.detail ?? null,
    adapter: l.adapter || null,
    setup: l.setup ?? null,
    missing: l.missing ?? [],
    // Only actions both sides know; an older hearthd sends none and meant Minecraft's.
    actions: (
      l.actions ?? (l.adapter || l.status === 'unsupported' ? [] : MINECRAFT_ACTIONS)
    ).filter((a): a is PlayerAction => (PLAYER_ACTIONS as readonly string[]).includes(a)),
    roster:
      l.roster?.map((p) => ({
        id: p.id,
        name: p.name,
        pingMs: p.ping_ms,
        connectedSeconds: p.connected_seconds,
      })) ?? null,
    queued: l.queued ?? null,
  }));

const MINECRAFT_ACTIONS: readonly PlayerAction[] = [
  'kick',
  'ban',
  'pardon',
  'op',
  'deop',
  'whitelist-add',
  'whitelist-remove',
];

export const serverLiveSchema = z.object({
  /** "ready" once the game answered; never a guess when it didn't. */
  status: z.enum(LIVE_STATUSES),
  /** RCON's own state: the player list is complete and actions work only when "ready". */
  rcon: z.enum(RCON_STATES),
  online: z.number().int(),
  max: z.number().int(),
  players: z.array(z.string()),
  /** False when `players` is the status ping's sample, which servers may cut short or hide. */
  playersComplete: z.boolean(),
  version: z.string().nullable(),
  /** Minecraft's message of the day; Rust's server name. */
  motd: z.string().nullable(),
  detail: z.string().nullable(),
  /** The game's live-data adapter ("minecraft", "rust"); null when unsupported. */
  adapter: z.string().nullable(),
  /**
   * Where RCON is set up: "server_properties" (the panel can turn it on with POST live/rcon)
   * or "startup" (the egg's variables, set by the owner on the Startup tab).
   */
  setup: z.enum(RCON_SETUPS).nullable(),
  /** For "startup" setups: the variables that are missing or invalid. */
  missing: z.array(z.string()),
  /** The actions this game supports, in display order. */
  actions: z.array(z.enum(PLAYER_ACTIONS)),
  /** Rust's detailed list: players are acted on by Steam ID, never by display name. */
  roster: z
    .array(
      z.object({
        id: z.string(),
        name: z.string(),
        pingMs: z.number().int(),
        connectedSeconds: z.number().int(),
      }),
    )
    .nullable(),
  /** Players waiting to join (Rust). */
  queued: z.number().int().nullable(),
});
export type ServerLive = z.infer<typeof serverLiveSchema>;

/** Java names, optionally with Floodgate's "." prefix for Bedrock players. */
export const playerNameSchema = z
  .string()
  .regex(/^\.?[A-Za-z0-9_]{1,16}$/, 'Not a valid player name.');

/**
 * A SteamID64 for an individual account (Rust). Rust display names are free text, so Rust
 * players are always addressed by this.
 */
export const steamIdSchema = z.string().regex(/^7656119[0-9]{10}$/, 'Not a valid Steam ID.');

export const playerActionParamsSchema = z.object({
  server: z.string(),
  /** A Minecraft name or a Steam ID; hearthd checks which one the game takes. */
  player: z.union([playerNameSchema, steamIdSchema]),
  action: z.enum(PLAYER_ACTIONS),
});

export const playerActionBodySchema = z.object({
  /** For kick and ban; other actions ignore it. */
  reason: z.string().trim().max(200).optional(),
});

export const playerActionResultSchema = z.object({
  /** The game's own reply, e.g. "Kicked Alex: spam". */
  reply: z.string(),
});

export const rconSetupResultSchema = z.object({
  /** Always true today: the game reads server.properties only when it starts. */
  restartRequired: z.boolean(),
  /** Which settings were written (names only; the password is never returned). */
  changed: z.array(z.string()),
});
